/* Ronini scroll bottle: the 316-frame drop / explode sequence drawn on one <canvas>.
   Why frames + canvas on every device (not the alpha video): seeking a VP9/HEVC alpha video on each scroll tick
   is asynchronous and decoder-bound — it lags a few frames behind the scroll, updates at the decoder's pace
   (~20-40 new images/s) and on throttled phones the seeks time out, so the old code fell back to this sequence
   anyway after a 2.5 s stall. Pre-decoded ImageBitmaps draw in <1 ms, so the frame on screen always matches the
   scroll position and the renderer can interpolate between neighbouring frames.
   - Frames are cropped to the bottle column (data crop) and served by the CDN at a height that fits the canvas.
   - Compressed bytes load progressively (every 8th frame, then 4th, 2nd, all), steered towards the scroll
     position; a window of frames around it is decoded off the main thread (createImageBitmap(blob)).
   - An rAF lerp eases the drawn position towards the scroll target and cross-fades adjacent frames
     ('lighter' = exact linear blend of premultiplied pixels); at rest it settles on a whole frame.
   - Redraws only when the drawn state changes; no layout reads in the loop (sizes are measured on refresh);
     nothing loads until the section is within ~0.6 viewport, and all work stops while it is off-screen. */
(function () {
  if (window.RoniniScrollFrames) return;
  const smooth = x => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

  window.RoniniScrollFrames = function (canvas, scene, options) {
    const opts = options || {};
    let data;
    try { data = JSON.parse(canvas.dataset.frames || 'null'); } catch (_) { data = null; }
    if (!data || !Array.isArray(data.v) || data.v.length < 2 || !window.createImageBitmap) return null;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return null;
    // The rendered sequence's full frame (files hold only the crop column) and the asset name prefix
    // (`<prefix><n>.webp`). Defaults = the original 2560x1440 "image<n>" set; the section passes its own.
    const FW = Number(data.fw) || 2560, FH = Number(data.fh) || 1440;
    const PREFIX = typeof data.prefix === 'string' && data.prefix ? data.prefix : 'image';
    const crop = data.crop || [0, 0, FW, FH];
    const mobile = window.matchMedia('(max-width: 991px)');
    // Phones use every 2nd frame (the cross-fade covers the gap): half the bytes and decode work.
    const step = opts.step || (mobile.matches ? 2 : 1);
    const blend = opts.blend !== false; // false: always draw the nearest whole frame (phones)
    const seq = [];
    for (let i = 0; i < data.v.length; i += step) seq.push(i);
    if (seq[seq.length - 1] !== data.v.length - 1) seq.push(data.v.length - 1);
    const count = seq.length;
    const gsap = window.gsap;

    // State.
    let destroyed = false, near = false, visible = false, running = false;
    let bucket = 0, cw = 0, ch = 0, dx = 0, dy = 0, dw = 0, dh = 0, epoch = 0;
    let raw = 0, target = 0, shown = -1, dir = 1, lastInput = 0, lastTick = 0, painted = '', paints = 0, drawnIndex = -1;
    const blobs = new Array(count).fill(null), failures = new Uint8Array(count);
    const fetching = new Set(), decoding = new Set(), bitmaps = new Map();
    let progressive = [], progPos = 0, starter = 0;
    // ImageBitmap.close() frees GPU/CPU memory synchronously (~1 ms each on a phone); batch it into idle time.
    const trash = [];
    let trashTimer = 0;
    const idle = window.requestIdleCallback
      ? fn => window.requestIdleCallback(fn, { timeout: 400 })
      : fn => setTimeout(() => fn({ timeRemaining: () => 8, didTimeout: false }), 60);
    function sweep(deadline) {
      let forced = 0; // under constant scrolling there may be no idle time: then free a few per timeout
      while (trash.length && (deadline.timeRemaining() > 2 || (deadline.didTimeout && forced++ < 3))) trash.pop().close();
      trashTimer = trash.length ? idle(sweep) : 0;
    }
    function release(bmp) {
      if (!bmp || !bmp.close) return;
      trash.push(bmp);
      if (!trashTimer) trashTimer = idle(sweep);
    }
    // 10-11 (owner: "still laggy"): desktop keeps a wider decoded window and more parallel fetches / decodes, so a
    // fast wheel flick lands on frames that are already bitmaps instead of the nearest loaded one.
    // 10-12: desktop decodes at ≤1.5x (bitmaps ~3 MB instead of ~6 MB at 2x), so a wider window fits in about the same
    // memory; every frame is drawn there now (no 2-frame cross-fade ghosting on the moving parts).
    const limits = () => mobile.matches
      ? { ahead: 24, behind: 8, cap: 40, fetches: 6, decodes: 3 }
      : { ahead: 44, behind: 14, cap: 60, fetches: 10, decodes: 6 };
    const first = () => (mobile.matches ? Math.min(Math.round(5 / step), count - 1) : 0);

    (function order() {
      const seen = new Uint8Array(count);
      for (const stride of [8, 4, 2, 1]) {
        for (let i = 0; i < count; i += stride) if (!seen[i]) { seen[i] = 1; progressive.push(i); }
        if (stride === 8) starter = progressive.length;
      }
    })();

    function url(i) {
      const f = seq[i];
      const name = PREFIX + (f + 1) + (bucket && bucket < FH ? '_x' + bucket : '') + '.webp';
      return data.base + name + '?v=' + data.v[f];
    }

    // --- layout (refresh / resize only) ---------------------------------------------------------------
    function measure() {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, mobile.matches ? 1.5 : 2);
      const cover = getComputedStyle(canvas).objectFit === 'cover';
      const pw = Math.round(w * dpr), ph = Math.round(h * dpr);
      const s = cover ? Math.max(pw / FW, ph / FH) : Math.min(pw / FW, ph / FH);
      const need = FH * s;
      const next = need <= 600 ? 540 : need <= 830 ? 720 : need <= 1200 ? 1080 : FH;
      if (bucket && next !== bucket) resetSources();
      bucket = next;
      if (pw !== cw || ph !== ch) { cw = canvas.width = pw; ch = canvas.height = ph; painted = ''; }
      const ox = (pw - FW * s) / 2, oy = (ph - FH * s) / 2;
      dx = ox + crop[0] * s; dy = oy + crop[1] * s; dw = crop[2] * s; dh = crop[3] * s;
    }
    function resetSources() {
      epoch += 1;
      blobs.fill(null); failures.fill(0); fetching.clear(); decoding.clear();
      bitmaps.forEach(release); bitmaps.clear();
      progPos = 0; painted = '';
    }
    function dropBitmaps() { bitmaps.forEach(release); bitmaps.clear(); decoding.clear(); epoch += 1; fetching.clear(); painted = ''; }

    // --- loading -------------------------------------------------------------------------------------------
    const center = () => Math.max(0, Math.min(count - 1, Math.round(shown < 0 ? target : shown)));
    function nextFetch() {
      const c = center(), L = limits();
      const ok = i => i >= 0 && i < count && !blobs[i] && !fetching.has(i) && failures[i] < 2;
      // Until the section is actually on screen: only the opening frames (not the whole look-ahead window, which
      // alone was 20+ files of ~300 KB on a phone that had not scrolled yet) ...
      const ahead = visible ? L.ahead : 3, behind = visible ? L.behind : 0;
      for (let d = 0; d <= ahead; d++) { const i = c + dir * d; if (ok(i)) return i; if (d && d <= behind && ok(c - dir * d)) return c - dir * d; }
      while (progPos < progressive.length && !ok(progressive[progPos])) progPos++;
      // ... then the first pass (every 8th frame, ~13% of the bytes), and that only once the page itself has loaded.
      if (!visible && (progPos >= starter || document.readyState !== 'complete')) return -1;
      if (progPos < progressive.length) return progressive[progPos];
      for (let i = 0; i < count; i++) if (ok(i)) return i; // retries skipped by the pointer (visible only)
      return -1;
    }
    // A24: two failed tries park a frame. Parked frames get a bounded second chance (3 rounds, with
    // a growing pause) and a fresh budget as soon as the network or the tab comes back, so a
    // sequence that started offline recovers without a reload. The cache limits are unchanged.
    let retryRounds = 0, retryTimer = 0;
    function retryParked() {
      retryTimer = 0;
      if (destroyed) return;
      failures.fill(0); progPos = 0; pump();
    }
    function scheduleRetry() {
      if (retryTimer || retryRounds >= 3 || destroyed) return;
      retryRounds += 1;
      retryTimer = setTimeout(retryParked, 4000 * retryRounds);
    }
    function recover() {
      if (destroyed || !failures.some(Boolean)) return;
      clearTimeout(retryTimer); retryRounds = 0;
      retryParked();
    }
    function fetchFrame(i) {
      const e = epoch; fetching.add(i);
      const done = blob => {
        if (e !== epoch || destroyed) return;
        fetching.delete(i);
        if (blob) blobs[i] = blob; else { failures[i] += 1; if (failures[i] >= 2) scheduleRetry(); }
        pump();
      };
      fetch(url(i), { headers: { Accept: 'image/webp,image/*;q=0.8' } })
        .then(r => (r.ok ? r.blob() : null))
        .then(b => (b && b.size ? b : null), () => null)
        .then(done);
    }
    function decodeFrame(i) {
      const e = epoch; decoding.add(i);
      createImageBitmap(blobs[i]).then(bmp => {
        if (e !== epoch || destroyed) { release(bmp); return; }
        decoding.delete(i);
        if (!near) { release(bmp); return; }
        bitmaps.set(i, bmp); evict(); pump();
        const c = shown < 0 ? target : shown;
        if (Math.abs(i - c) <= 2 || drawnIndex < 0 || Math.abs(i - c) < Math.abs(drawnIndex - c)) { painted = ''; kick(); }
      }, () => { if (e !== epoch) return; decoding.delete(i); blobs[i] = null; failures[i] += 1; pump(); });
    }
    function evict() {
      const L = limits();
      if (bitmaps.size <= L.cap) return;
      const c = center();
      [...bitmaps.keys()].sort((a, b) => Math.abs(b - c) - Math.abs(a - c)).slice(0, bitmaps.size - L.cap).forEach(i => {
        release(bitmaps.get(i)); bitmaps.delete(i);
      });
    }
    function pump() {
      if (destroyed || !near || document.hidden || !bucket) return;
      const L = limits(), c = center();
      while (fetching.size < L.fetches) { const i = nextFetch(); if (i < 0) break; fetchFrame(i); }
      // Decode nearest-first inside the window, biased towards the scroll direction.
      for (let d = 0; d <= L.ahead && decoding.size < L.decodes; d++) {
        for (const i of d && d <= L.behind ? [c + dir * d, c - dir * d] : [c + dir * d]) {
          if (i >= 0 && i < count && blobs[i] && !bitmaps.has(i) && !decoding.has(i) && decoding.size < L.decodes) decodeFrame(i);
        }
      }
    }

    // --- drawing -------------------------------------------------------------------------------------------
    function nearest(i) {
      if (bitmaps.has(i)) return i;
      let best = -1, bd = Infinity;
      bitmaps.forEach((_, k) => { const d = Math.abs(k - i); if (d < bd) { bd = d; best = k; } });
      return best;
    }
    function render() {
      if (!cw || !bitmaps.size) return;
      const base = Math.floor(shown), f = shown - base;
      // Cross-fade over the middle 80% of each step (eased; a short hold at each whole frame), quantised to 1/24.
      let w = Math.round(smooth((f - 0.1) / 0.8) * 24) / 24;
      let a = bitmaps.has(base) ? base : -1, b = bitmaps.has(base + 1) ? base + 1 : -1;
      if (w > 0 && w < 1 && (a < 0 || b < 0)) { a = nearest(Math.round(shown)); b = -1; w = 0; }
      else if (w >= 1) { a = b >= 0 ? b : nearest(base + 1); b = -1; w = 0; }
      else if (w <= 0) { a = a >= 0 ? a : nearest(base); b = -1; w = 0; }
      if (a < 0) return;
      if (!blend && b >= 0) { if (w >= 0.5) a = b; b = -1; w = 0; }
      const key = a + ':' + b + ':' + w + ':' + cw + 'x' + ch;
      if (key === painted) return;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, cw, ch);
      if (b >= 0) {
        ctx.globalAlpha = 1 - w; ctx.drawImage(bitmaps.get(a), dx, dy, dw, dh);
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = w; ctx.drawImage(bitmaps.get(b), dx, dy, dw, dh);
        ctx.globalCompositeOperation = 'source-over';
      } else {
        ctx.globalAlpha = 1; ctx.drawImage(bitmaps.get(a), dx, dy, dw, dh);
      }
      ctx.globalAlpha = 1;
      painted = key; paints += 1; drawnIndex = w >= 0.5 && b >= 0 ? b : a;
      if (!canvas.classList.contains('is-drawn')) canvas.classList.add('is-drawn');
    }
    // Easing towards the scroll target. With Lenis (desktop) the scroll value is already smoothed, so only a light
    // follow is added (two full lerps on top of each other felt rubbery); touch scroll gets a longer one, and a speed
    // cap (frames per second of the drawn sequence) so a hard fling does not race through the explode.
    const TAU = Number(opts.tau) || 85;
    const MAX_RATE = Number(opts.maxRate) || 0;
    function tick() {
      if (destroyed) return stop();
      const now = performance.now();
      const dt = Math.min(64, now - (lastTick || now - 16)); lastTick = now;
      // Settle on a whole frame once scrolling stops, so a still page never shows a blend.
      target = now - lastInput > 140 ? Math.round(raw) : raw;
      if (shown < 0) shown = target;
      const diff = target - shown;
      let move = Math.abs(diff) < 0.01 ? diff : diff * (1 - Math.exp(-dt / TAU));
      if (MAX_RATE) { const lim = MAX_RATE * dt / 1000; if (move > lim) move = lim; else if (move < -lim) move = -lim; }
      shown = Math.abs(diff) < 0.01 ? target : shown + move;
      const c = center();
      if (c !== tick.c) { tick.c = c; pump(); }
      render();
      if (shown === target && target === Math.round(raw) && (painted || !bitmaps.size)) stop();
    }
    function start() {
      if (running || destroyed || !visible || document.hidden) return;
      running = true; lastTick = 0;
      if (gsap && gsap.ticker) gsap.ticker.add(tick); else (function loop() { if (!running) return; tick(); requestAnimationFrame(loop); })();
    }
    function stop() { if (!running) return; running = false; if (gsap && gsap.ticker) gsap.ticker.remove(tick); }
    function kick() { if (visible) start(); }

    // --- public -------------------------------------------------------------------------------------------
    function setProgress(p) {
      p = Math.max(0, Math.min(1, Number(p) || 0));
      const s0 = first(), next = s0 + p * (count - 1 - s0);
      if (next !== raw) dir = next > raw ? 1 : -1;
      raw = next; lastInput = performance.now();
      if (shown < 0 && !running) target = raw;
      kick();
    }
    function refresh() { measure(); painted = ''; recover(); pump(); kick(); }
    const onVisibility = () => { if (document.hidden) stop(); else { recover(); pump(); kick(); } };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', recover);
    if (document.readyState !== 'complete') window.addEventListener('load', () => pump(), { once: true });
    let ioNear, ioVisible;
    if (window.IntersectionObserver) {
      ioNear = new IntersectionObserver(es => {
        near = es.some(e => e.isIntersecting);
        if (near) { if (!bucket) measure(); pump(); } else { stop(); dropBitmaps(); }
      }, { rootMargin: (opts.lead || '60%') + ' 0px' });
      ioVisible = new IntersectionObserver(es => { visible = es.some(e => e.isIntersecting); if (visible) { painted = ''; pump(); kick(); } else stop(); });
      ioNear.observe(scene); ioVisible.observe(scene);
    } else { near = visible = true; }
    measure();

    return {
      setProgress, refresh,
      mobile: mobile.matches,
      debug: () => ({ shown: drawnIndex < 0 ? -1 : seq[drawnIndex], value: shown * step, target: raw * step, paints, decoded: bitmaps.size, loaded: blobs.filter(Boolean).length, bucket }),
      destroy() {
        destroyed = true; stop(); ioNear && ioNear.disconnect(); ioVisible && ioVisible.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('online', recover); clearTimeout(retryTimer);
        resetSources(); ctx.clearRect(0, 0, cw, ch); canvas.classList.remove('is-drawn');
      }
    };
  };
})();
